// src/i18n.js
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpApi from "i18next-http-backend";
import Cookies from "js-cookie";

// Import translation files
import translationEN from "./locales/en/translation.json";
import translationAR from "./locales/ar/translation.json";

// Translation resources
const resources = {
  en: { translation: translationEN },
  ar: { translation: translationAR },
};

// Function to update lang/dir on <html> based on language
function updateDirection(language) {
  const htmlElement = document.documentElement;
  htmlElement.setAttribute("lang", language.startsWith("ar") ? "ar" : "en");
  htmlElement.setAttribute("dir", language.startsWith("ar") ? "rtl" : "ltr");
}

// UAT-CS-01: this used to only run once, after init resolved, and only from the
// Promise below — so a page that changed language via a different path (there
// used to be several: Header's own toggle, languageUtils.changeLanguage, this
// module's own changeLanguage export) had to remember to set dir itself, and
// direction was briefly wrong on first load until the detector resolved. Binding
// this to i18next's own 'languageChanged' event instead means it fires for every
// resolution — the initial one AND every subsequent switch, from any call site —
// so it can't drift out of sync again.
i18n.on("languageChanged", updateDirection);

// Initialize i18n
i18n
  .use(initReactI18next)
  .use(HttpApi)
  .use(LanguageDetector)
  .init({
    // No `lng` here on purpose: i18next treats an explicit `lng` as an override and
    // skips LanguageDetector entirely, so every fresh load/refresh ignored whatever
    // language the user had actually picked (the cookie LanguageDetector caches to
    // on every changeLanguage() call) and reset to English. fallbackLng only kicks
    // in when detection finds nothing at all.
    fallbackLng: 'en',
    resources,
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      // "htmlTag" deliberately left out: public/index.html has a static, hardcoded
      // <html lang="en">, baked in at build time with no server render to vary it —
      // with htmlTag checked first, it always won over the actual saved preference
      // below it in this list, which is the other half of why refresh reset to English.
      order: ["cookie", "localStorage", "sessionStorage", "navigator", "path", "subdomain"],
      // Cache to both: the cookie is what this app has always read back on init,
      // and localStorage (same 'app_language' key languageUtils.ts already writes
      // on every manual switch) is a second copy that survives cookie clearing —
      // by the browser, by extensions, or by app code — so a user's choice isn't
      // lost just because one of the two stores got wiped.
      caches: ["cookie", "localStorage"],
      lookupLocalStorage: "app_language",
    },
  });

// Function to change language and update direction
export function changeLanguage(language) {
  i18n.changeLanguage(language); // Notify i18next of the language change — 'languageChanged' (above) updates dir/lang
}

export default i18n;
